import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { NumberParam, type QueryParamConfigMap } from 'serialize-query-params';

import { GetRolesParams, useGetRolesInfinite } from '@/api/generated';
import DataGrid from '@/components/DataGrid/DataGrid';
import Field from '@/components/fields/Field';
import SearchInput from '@/components/fields/SearchInput';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';

import { roleColumns } from './roleColumns';

const DEFAULT_VALUES = {
  name: '',
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
};

const URL_CONFIG_MAP = {
  size: NumberParam
} satisfies QueryParamConfigMap;

const Roles = () => {
  const { t } = useTranslation(['users', 'common']);
  const [queryBody, setQueryBody] = useState<GetRolesParams | null>(null);

  const {
    data: rolesData,
    isError,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetRolesInfinite(queryBody ?? {}, {
    query: { suspense: false, enabled: !!queryBody }
  });

  const rolesList = useMemo(() => rolesData?.pages?.flatMap(page => page?.roles || []) || [], [rolesData]);

  const handleFetchNextPage = useCallback(() => {
    if (isError || isFetchingNextPage || !hasNextPage || !rolesData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, rolesData?.pages?.length]);

  const { form, sorting, handleChangeSort } = useEntityList(
    DEFAULT_VALUES,
    URL_CONFIG_MAP,
    setQueryBody,
    undefined,
    300
  );

  const columns = useMemo(() => roleColumns(t), [t]);

  return (
    <Stack>
      <StickyHead sx={{ pt: 2.25 }}>
        <Stack alignItems="start">
          <Typography variant="h2">{t('roles.title')}</Typography>
          <Form form={form} className="Filters-root">
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
              <Field
                name="name"
                component={SearchInput}
                innerProps={{
                  placeholder: t('users.searchPlaceholder'),
                  sx: { minWidth: 320, bgcolor: 'background.default' }
                }}
              />
            </Stack>
          </Form>
        </Stack>
      </StickyHead>
      <Box sx={{ px: 5, flexGrow: 1 }}>
        <DataGrid
          data={rolesList}
          columns={columns}
          isLoading={isLoading}
          isLoadingNextPage={isFetchingNextPage}
          onSort={handleChangeSort}
          hasNextPage={hasNextPage}
          sorting={sorting}
          resizeWatchSelector=".Filters-root"
          hasError={isError && !rolesList.length}
          onLoadNextPage={handleFetchNextPage}
        />
      </Box>
    </Stack>
  );
};

export default Roles;
